<template>
  <div>
    <div class="d-flex justify-content-between align-items-center pt-50 mb-2">
      <h2 class="font-weight-bolder m-0">
        <span @click="$router.go(-1)">
          <b-button
            variant="white"
            class="btn-icon rounded-circle mr-50 text-primary bg-white"
            size="sm"
          >
            <feather-icon icon="ChevronLeftIcon" />
          </b-button>
        </span> Calendario laboral
      </h2>
      <b-button
        variant="primary"
        class="ml-2"
        @click="showCreateModal = true"
      >
        Añadir calendario
      </b-button>
    </div>

    <!-- Modal para crear calendario -->
    <b-modal
      v-model="showCreateModal"
      title="Nuevo calendario"
      size="md"
      centered
      hide-footer
      no-close-on-backdrop
      @hidden="showCreateModal = false"
    >
      <create-calendar
        :client-id="currentClient.id"
        @success="showCreateModal = false"
        @cancel="showCreateModal = false"
      />
    </b-modal>

    <div class="mb-3">
      <template v-if="calendars && calendars.length">
        <b-row>
          <b-col
            v-for="calendar in calendars"
            :key="calendar.id"
            cols="12"
            md="6"
            class="mb-2"
          >
            <b-card
              class="h-100"
              :class="{ 'border-primary': calendar.is_default }"
            >
              <div class="d-flex justify-content-between align-items-start mb-1">
                <div>
                  <h4 class="mb-0">
                    {{ calendar.name }}
                  </h4>
                  <b-badge
                    v-if="calendar.is_default"
                    variant="light-primary"
                    class="mt-50"
                  >
                    Por defecto
                  </b-badge>
                </div>
                <b-dropdown
                  variant="link"
                  no-caret
                  toggle-class="p-0"
                  right
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="18"
                      class="text-body"
                    />
                  </template>
                  <b-dropdown-item v-if="!calendar.is_default" @click="toggleCalendarDefault(calendar)">
                    <feather-icon
                      :icon="calendar.is_default ? 'StarIcon' : 'StarIcon'"
                      size="16"
                      :class="calendar.is_default ? 'text-warning' : ''"
                    />
                    <span class="ml-50">{{ calendar.is_default ? 'Quitar por defecto' : 'Hacer por defecto' }}</span>
                  </b-dropdown-item>
                  <b-dropdown-divider v-if="!calendar.is_default" />
                  <b-dropdown-item @click="viewHolidays(calendar.id)">
                    <feather-icon
                      icon="EyeIcon"
                      size="16"
                    />
                    <span class="ml-50">Ver festivos</span>
                  </b-dropdown-item>
                  <b-dropdown-divider />
                  <b-dropdown-item
                    variant="danger"
                    @click="deleteCalendar(calendar.id, calendar.name)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="16"
                    />
                    <span class="ml-50">Eliminar</span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>

              <b-card-text class="mt-1">
                <div
                  v-if="calendar.autonomous_community"
                  class="d-flex align-items-center mb-75"
                >
                  <feather-icon
                    icon="MapIcon"
                    size="16"
                    class="text-muted mr-50"
                  />
                  <span class="font-weight-bold">Comunidad:</span>
                  <span class="ml-50">{{ calendar.autonomous_community }}</span>
                </div>

                <div class="d-flex align-items-center mb-75">
                  <feather-icon
                    :icon="calendar.sync_public_holidays ? 'RefreshCwIcon' : 'XCircleIcon'"
                    size="16"
                    :class="calendar.sync_public_holidays ? 'text-success' : 'text-muted'"
                    class="mr-50"
                  />
                  <span class="font-weight-bold">Sincronización:</span>
                  <span class="ml-50">
                    {{ calendar.sync_public_holidays ? 'Activa' : 'Inactiva' }}
                  </span>
                </div>

                <div class="d-flex align-items-center">
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="text-muted mr-50"
                  />
                  <span class="font-weight-bold">Festivos:</span>
                  <span class="ml-50">
                    {{ calendar.holidays ? calendar.holidays.length : 0 }} días
                  </span>
                </div>

                <div
                  v-if="calendar.users && calendar.users.length > 0"
                  class="d-flex align-items-center mt-75"
                >
                  <feather-icon
                    icon="UsersIcon"
                    size="16"
                    class="text-muted mr-50"
                  />
                  <span class="font-weight-bold">Usuarios:</span>
                  <span class="ml-50">{{ calendar.users.length }}</span>
                </div>
              </b-card-text>
            </b-card>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <b-card>
          <div class="text-center text-muted py-3">
            Aún no se ha creado ningún calendario.
          </div>
        </b-card>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  BButton,
  BCard,
  BRow,
  BCol,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BCardText,
  BModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import CreateCalendar from './Create.vue'

export default {
  components: {
    BButton,
    BCard,
    BRow,
    BCol,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BCardText,
    BModal,
    CreateCalendar,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      showCreateModal: false,
    }
  },
  computed: {
    ...mapGetters({
      currentClient: 'clients/getCurrentClient',
      canEditModuleVacations: 'clients/getCanEditModuleVacations',
      calendars: 'calendar/getCalendars',
    }),
  },
  async created() {
    await this.listCalendars({ client_id: this.currentClient.id })
  },
  methods: {
    ...mapActions({
      listCalendars: 'calendar/list',
      update: 'calendar/update',
      delete: 'calendar/delete',
    }),
    viewHolidays(calendarId) {
      this.$router.push({ name: 'calendario-festivos', params: { id: calendarId } })
    },
    async toggleCalendarDefault(calendar) {
      // Alternar el estado de is_default del calendario seleccionado
      // El backend se encarga de quitar is_default de los otros calendarios
      const newValue = calendar.is_default === 1 ? 0 : 1
      await this.update({ id: calendar.id, data: { is_default: newValue } })
      await this.listCalendars({ client_id: this.currentClient.id })
    },
    deleteCalendar(id, name) {
      this.$bvModal
        .msgBoxConfirm(`¿Estás seguro de eliminar ${name}?`, {
          bodyClass: 'bodyDelete',
          footerClass: 'footerDelete',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          cancelVariant: 'outline-danger',
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.delete(id)
          }
        })
    },
  },
}
</script>

<style lang="scss">
</style>
